import React, { useRef } from "react";
import { Button } from "@/components/ui/button";
import { UploadSimple, Warning } from "@phosphor-icons/react";

/**
 * ExcelUploadZone — drag-and-drop + click-to-pick file input for the Realisasi
 * upload flow. Owns UX chrome only; parent handles the actual upload/parse.
 */
export default function ExcelUploadZone({
  uploading,
  uploadPreview,
  dragOver,
  setDragOver,
  onFileSelected,
}) {
  const inputRef = useRef(null);

  const handleFile = (file) => {
    if (file) onFileSelected(file);
  };

  const onInputChange = (e) => handleFile(e.target.files?.[0]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer?.files?.[0]);
    // reset input so choosing the same file twice still triggers change
    if (inputRef.current) inputRef.current.value = "";
  };

  const openPicker = () => !uploading && inputRef.current?.click();

  return (
    <div className="mb-6" data-testid="excel-upload-section">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={openPicker}
        className={`border-2 border-dashed p-8 md:p-12 text-center transition-colors cursor-pointer select-none ${
          dragOver
            ? "border-primary bg-primary/10"
            : "border-primary/40 bg-primary/5 hover:bg-primary/10"
        }`}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && openPicker()}
        data-testid="excel-upload-dropzone"
      >
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xlsm"
          onChange={onInputChange}
          className="hidden"
          data-testid="excel-upload-input"
        />
        <UploadSimple size={44} weight="bold" className="mx-auto mb-3 text-primary" />
        <div className="text-lg font-black tracking-tight mb-1">
          {uploading ? "Parsing file..." : dragOver ? "Lepaskan file di sini" : "Drag & drop file Excel"}
        </div>
        <div className="text-sm text-muted-foreground mb-4">
          {uploading ? "Mohon tunggu..." : "atau klik untuk pilih file .xlsx / .xlsm"}
        </div>
        {!uploading && (
          <Button
            onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
            className="rounded-none font-bold uppercase tracking-wider"
            data-testid="excel-upload-button"
          >
            <UploadSimple size={16} className="mr-2" />
            Pilih File Excel
          </Button>
        )}
        {uploadPreview && !uploading && (
          <div className="text-xs text-muted-foreground font-mono mt-4 pt-4 border-t border-border">
            File: <span className="font-bold text-foreground">{uploadPreview.filename}</span> · {uploadPreview.total_rows_in_file} baris · Expires {new Date(uploadPreview.expires_at).toLocaleTimeString()}
          </div>
        )}
      </div>
      <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1">
        <Warning size={12} weight="bold" className="text-amber-600" />
        File TIDAK disimpan di server. Hanya rows di-cache di DB max 30 menit sampai Anda klik &quot;Terapkan&quot;.
      </p>
    </div>
  );
}
